import { Box, SvgIcon } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PieChartIcon from "@mui/icons-material/PieChart";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import RefreshIcon from "@mui/icons-material/Refresh";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import IconButton from "../common/IconButton";
import OutlinedButton from "../common/OutlinedButton";
import Alert from "../common/Alert";
import ToggleButton from "../common/ToggleButton";
import Eligibility from "../common/Eligibility";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  selectPrices,
  selectProductDetail,
  selectQuickInfo,
} from "../../../../features/productAnalyzer/productAnalyzer.selector";
import { PurchaseMenu, ReplenishMenu } from "../../../../components";
import {
  addProductHistory,
  updateProductDetail,
} from "../../../../features/productAnalyzer/productAnalyzer.slice";
import { IProductHistory } from "../../../../features/productAnalyzer/productAnalyzer.types";
import { updateProduct } from "../../../../services/apiV2";
import { enqueueSnackbar } from "notistack";
import { Notifications } from "../../../../constants";
import { selectExtension } from "../../../../features/extension/extension.selector";
import { TAmazonEmbededViewType } from "..";
import CalculateIcon from "@mui/icons-material/Calculate";
import { updateExtension } from "../../../../features/extension/extension.slice";

const MESSAGES = {
  EMBED_CENTER_CLOSED: "embed_center_closed",
  DOCK_RIGHT: "dock_right",
} as const;

export default function Header({
  view,
  setView,
}: {
  view: TAmazonEmbededViewType;
  setView: (view: TAmazonEmbededViewType) => void;
}) {
  const dispatch = useAppDispatch();
  const { mode } = useAppSelector(selectExtension);
  const { productDetail } = useAppSelector(selectProductDetail);
  const { salePrice, costPrice } = useAppSelector(selectPrices);
  const { asin, isFavorite, isPurchased, isReplenished } = productDetail ?? {};
  const { quickInfo } = useAppSelector(selectQuickInfo);
  const [purchaseMenuEl, setPurchaseMenuEl] = useState<null | HTMLElement>(
    null,
  );
  const [replenishMenuEl, setReplenishMenuEl] = useState<null | HTMLElement>(
    null,
  );
  const [isUpdatingFavorite, setIsUpdatingFavorite] = useState<boolean>(false);
  const isPurchaseMenuOpened = Boolean(purchaseMenuEl);
  const isReplenishMenuOpened = Boolean(replenishMenuEl);

  const handleAddHistory = (data: IProductHistory) => {
    dispatch(addProductHistory(data));
  };

  const handleFavoriteClick = () => {
    setIsUpdatingFavorite(true);
    updateProduct({ asin: asin ?? "", isFavorite: !isFavorite })
      .then(() => {
        dispatch(updateProductDetail({ isFavorite: !isFavorite }));
      })
      .catch((e) => {
        console.error(e);
        enqueueSnackbar(Notifications.PA_UPDATE_ERROR, { variant: "error" });
      })
      .finally(() => {
        setIsUpdatingFavorite(false);
      });
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      py={1}
      flexWrap="wrap"
      gap={0.5}
    >
      <Box display="flex" alignItems="center" gap={0.5}>
        <IconButton
          onClick={() =>
            window.open(
              `${process.env.REACT_APP_SERVER_URL}/settings`,
              "_blank",
            )
          }
          tooltip="Settings"
        >
          <SvgIcon component={SettingsIcon} sx={{ fontSize: "16px" }} />
        </IconButton>
        {view === "profitCalculator" && (
          <IconButton
            onClick={() => setView("buyboxAnalysis")}
            tooltip="Buy Box Analysis"
          >
            <SvgIcon component={PieChartIcon} sx={{ fontSize: "16px" }} />
          </IconButton>
        )}
        {view === "buyboxAnalysis" && (
          <IconButton
            onClick={() => setView("profitCalculator")}
            tooltip="Profit Calculator"
          >
            <SvgIcon component={CalculateIcon} sx={{ fontSize: "17px" }} />
          </IconButton>
        )}
        <OutlinedButton
          onClick={() =>
            dispatch(updateExtension({ amazonEmbededView: "full" }))
          }
        >
          Full Analysis
        </OutlinedButton>
      </Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Alert variant="danger" type="amazonShareBuyBox" />
        <Alert variant="warning" type="privateLabel" />
        <ToggleButton
          toggled={isPurchased ? true : false}
          onToggle={(event: React.MouseEvent<HTMLButtonElement>) =>
            setPurchaseMenuEl(event.currentTarget)
          }
          tooltip="Add purchased quantity"
        >
          <AddShoppingCartIcon />
        </ToggleButton>
        <ToggleButton
          toggled={isReplenished ? true : false}
          onToggle={(event: React.MouseEvent<HTMLButtonElement>) =>
            setReplenishMenuEl(event.currentTarget)
          }
          tooltip="Add replenished quantity"
        >
          <RefreshIcon />
        </ToggleButton>
        <ToggleButton
          toggled={isFavorite ? true : false}
          loading={isUpdatingFavorite}
          onToggle={handleFavoriteClick}
          tooltip={isFavorite ? "Remove from favorite" : "Add to favorite"}
        >
          <StarOutlineIcon />
        </ToggleButton>
        <Eligibility
          eligibility={{
            eligible: quickInfo?.eligibility?.eligible ?? "Unknown",
            hint: quickInfo?.eligibility?.hint,
          }}
        />
      </Box>
      <PurchaseMenu
        open={isPurchaseMenuOpened}
        anchorEl={purchaseMenuEl}
        meta={{ asin: asin ?? "", salePrice, costPrice }}
        onClose={() => setPurchaseMenuEl(null)}
        onPurchase={handleAddHistory}
      />
      <ReplenishMenu
        open={isReplenishMenuOpened}
        anchorEl={replenishMenuEl}
        meta={{ asin: asin ?? "", salePrice, costPrice }}
        onClose={() => setReplenishMenuEl(null)}
        onReplenish={handleAddHistory}
      />
    </Box>
  );
}
